import './draggable-native.scss'
import platform from 'platform'
import React, {forwardRef, ReactElement, useRef} from 'react'
import {Util} from 'yougile-util'
import {DragData, DragDataObject} from '@common/drag-data'

type DragEvent = React.DragEvent

const defaultGhostCreator = (el: HTMLElement) => {
  return el.cloneNode(true) as HTMLElement
}

export interface DraggableProps extends React.HTMLProps<HTMLDivElement> {
  transfer?: DragDataObject
  onDragStarted?: (el: HTMLElement, e: React.DragEvent) => void
  onDragStartedCallback?: () => void
  onDragEnded?: (el: HTMLElement, e: React.DragEvent) => void
  emptyGhost?: boolean
  cloneElement?: boolean
  className?: string
  children: ReactElement
  style?: React.CSSProperties
  draggable?: boolean
  createGhost?: (el: HTMLElement) => HTMLElement | null
  /**
   * Force disable behavior of adding dragging and dragged class, set opacity while dragging
   */
  noStyleEffects?: boolean
  /**
   * Ghost element will not be deleted after drag ended
   */
  dontDeleteGhost?: boolean
  getDragImageOffset?: (el: HTMLElement, e: DragEvent) => {x: number; y: number}
}

const isSafariOnMac = Util.isMac() && platform.name === 'Safari'

export const DraggableNative = forwardRef<HTMLDivElement, DraggableProps>(
  (props: DraggableProps, ref) => {
    const {
      createGhost = defaultGhostCreator,
      noStyleEffects,
      dontDeleteGhost = false,
      getDragImageOffset,
      cloneElement = false,
      children,
      transfer,
      onDragStarted,
      onDragStartedCallback,
      onDragEnded,
      ...rest
    } = props
    const isDraggable = props.draggable !== undefined ? props.draggable : true
    const ghostRef = useRef<HTMLElement | null>(null)

    if (!isDraggable || !props.transfer) {
      return props.children
    }

    const onDragStart = (e: DragEvent) => {
      const el = e.target as HTMLElement
      onDragStarted?.(el, e)
      DragData.set(e, transfer)

      const nodeRect = el.getBoundingClientRect()
      const ghostElement = createGhost(el) || defaultGhostCreator(el)
      ghostRef.current = ghostElement
      ghostElement.classList.add('ghost')
      if (isSafariOnMac) {
        ghostElement.style.visibility = 'visible'
      }
      ghostElement.style.position = 'absolute'
      ghostElement.style.zIndex = '1000000'
      ghostElement.style.left = isSafariOnMac ? `0px` : `-10000px`
      ghostElement.style.top = isSafariOnMac ? `0px` : `-10000px`
      ghostElement.style.pointerEvents = 'none'
      document.body.appendChild(ghostElement)

      const offset = getDragImageOffset
        ? getDragImageOffset(el, e)
        : {x: e.clientX - nodeRect.left, y: e.clientY - nodeRect.top}

      e.dataTransfer.setDragImage(ghostElement, offset.x, offset.y)

      if (isSafariOnMac) {
        setTimeout(() => {
          ghostElement.style.visibility = 'hidden'
        })
      }

      if (!noStyleEffects) {
        el.classList.add('dragging')
        el.style.opacity = '0.999'
      }

      requestAnimationFrame(() => {
        if (!noStyleEffects) {
          el?.classList.add('dragged')
        }
        onDragStartedCallback?.()
      })
    }
    const onDragEnd = (e: DragEvent) => {
      const el = e.target as HTMLElement
      const ghostElement = ghostRef.current
      if (!dontDeleteGhost) {
        ghostElement?.remove()
      }

      onDragEnded?.(el, e)
      el.style.opacity = ''
      if (el && !noStyleEffects) {
        el.classList.remove('dragged')
        el.classList.remove('dragging')
      }
    }

    if (cloneElement && children) {
      return React.cloneElement(children, {
        onDragStart,
        onDragEnd,
        ref
      })
    }

    return (
      <div
        {...rest}
        ref={ref}
        draggable={isDraggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        {children}
      </div>
    )
  }
)
